"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createItemFactory = exports.updateItemFactory = exports.getItemFactory = exports.defaultDeleteItem = exports.defaultUpdateItem = exports.defaultCreateItem = exports.defaultGetItem = exports.useCrudSource = void 0;
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const react_intl_1 = require("react-intl");
const snackbar_context_1 = require("../composite/snackbar/snackbar-context");
const snackbar_types_1 = require("../composite/snackbar/snackbar-types");
const abortable_fetch_1 = require("./abortable-fetch");
const event_callback_hook_1 = require("./event-callback-hook");
function useCrudSource({ url, getItem = defaultGetItem, createItem = defaultCreateItem, updateItem = defaultUpdateItem, deleteItem = defaultDeleteItem, handleGetError, handleCreateError, handleUpdateError, handleDeleteError, getMessages, createMessages, updateMessages, delMessages, }) {
    const [loading, setLoading] = react_1.useState(false);
    const [data, setData] = react_1.useState(null);
    const fetch = react_1.useRef(null);
    const intl = react_intl_1.useIntl();
    const { showSnackbar } = react_1.useContext(snackbar_context_1.SnackbarContext);
    const handleGet = event_callback_hook_1.useEventCallback(async (id) => {
        var _a;
        try {
            setLoading(true);
            if (fetch.current !== null) {
                fetch.current.abort();
            }
            fetch.current = getItem(url, id);
            const data = await fetch.current.json();
            react_dom_1.unstable_batchedUpdates(() => {
                setData(data);
                setLoading(false);
            });
            return data;
        }
        catch (err) {
            setLoading(false);
            if (err.name !== 'AbortError') {
                if (handleGetError !== undefined) {
                    handleGetError(err);
                }
                else {
                    const message = (_a = getMessages === null || getMessages === void 0 ? void 0 : getMessages.errorMessage) !== null && _a !== void 0 ? _a : intl.formatMessage({
                        id: 'EAS_CRUD_SOURCE_MSG_LOAD_ERROR',
                        defaultMessage: 'Chyba načtení dat: {detail}',
                    });
                    showSnackbar(message, snackbar_types_1.SnackbarVariant.ERROR);
                    throw err;
                }
            }
            return undefined;
        }
    });
    const handleCreate = event_callback_hook_1.useEventCallback(async (item) => {
        var _a, _b;
        try {
            setLoading(true);
            if (fetch.current !== null) {
                fetch.current.abort();
            }
            fetch.current = createItem(url, item);
            const data = await fetch.current.json();
            const message = (_a = createMessages === null || createMessages === void 0 ? void 0 : createMessages.successMessage) !== null && _a !== void 0 ? _a : intl.formatMessage({
                id: 'EAS_CRUD_SOURCE_MSG_CREATE_SUCCESS',
                defaultMessage: 'Záznam byl úspěšně vytvořen.',
            });
            react_dom_1.unstable_batchedUpdates(() => {
                showSnackbar(message, snackbar_types_1.SnackbarVariant.SUCCESS);
                setData(data);
                setLoading(false);
            });
            return data;
        }
        catch (err) {
            setLoading(false);
            if (err.name !== 'AbortError') {
                if (handleCreateError !== undefined) {
                    handleCreateError(err);
                }
                else {
                    const message = (_b = createMessages === null || createMessages === void 0 ? void 0 : createMessages.errorMessage) !== null && _b !== void 0 ? _b : intl.formatMessage({
                        id: 'EAS_CRUD_SOURCE_MSG_CREATE_ERROR',
                        defaultMessage: 'Chyba uložení dat: {detail}',
                    }, { detail: err.message });
                    showSnackbar(message, snackbar_types_1.SnackbarVariant.ERROR);
                    throw err;
                }
            }
            return undefined;
        }
    });
    const handleUpdate = event_callback_hook_1.useEventCallback(async (item, prevItem) => {
        var _a, _b;
        try {
            setLoading(true);
            if (fetch.current !== null) {
                fetch.current.abort();
            }
            fetch.current = updateItem(url, item, prevItem);
            const data = await fetch.current.json();
            const message = (_a = updateMessages === null || updateMessages === void 0 ? void 0 : updateMessages.successMessage) !== null && _a !== void 0 ? _a : intl.formatMessage({
                id: 'EAS_CRUD_SOURCE_MSG_UPDATE_SUCCESS',
                defaultMessage: 'Záznam byl úspěšně upraven.',
            });
            react_dom_1.unstable_batchedUpdates(() => {
                showSnackbar(message, snackbar_types_1.SnackbarVariant.SUCCESS);
                setData(data);
                setLoading(false);
            });
            return data;
        }
        catch (err) {
            setLoading(false);
            if (err.name !== 'AbortError') {
                if (handleUpdateError !== undefined) {
                    handleUpdateError(err);
                }
                else {
                    const message = (_b = updateMessages === null || updateMessages === void 0 ? void 0 : updateMessages.errorMessage) !== null && _b !== void 0 ? _b : intl.formatMessage({
                        id: 'EAS_CRUD_SOURCE_MSG_UPDATE_ERROR',
                        defaultMessage: 'Chyba uložení dat: {detail}',
                    }, { detail: err.message });
                    showSnackbar(message, snackbar_types_1.SnackbarVariant.ERROR);
                    throw err;
                }
            }
            return undefined;
        }
    });
    const handleDelete = event_callback_hook_1.useEventCallback(async (id) => {
        var _a, _b;
        try {
            setLoading(true);
            if (fetch.current !== null) {
                fetch.current.abort();
            }
            fetch.current = deleteItem(url, id);
            await fetch.current.none();
            const message = (_a = delMessages === null || delMessages === void 0 ? void 0 : delMessages.successMessage) !== null && _a !== void 0 ? _a : intl.formatMessage({
                id: 'EAS_CRUD_SOURCE_MSG_DELETE_SUCCESS',
                defaultMessage: 'Záznam byl úspěšně smazán.',
            });
            react_dom_1.unstable_batchedUpdates(() => {
                showSnackbar(message, snackbar_types_1.SnackbarVariant.SUCCESS);
                setData(null);
                setLoading(false);
            });
        }
        catch (err) {
            setLoading(false);
            if (err.name !== 'AbortError') {
                if (handleDeleteError !== undefined) {
                    handleDeleteError(err);
                }
                else {
                    const message = (_b = delMessages === null || delMessages === void 0 ? void 0 : delMessages.successMessage) !== null && _b !== void 0 ? _b : intl.formatMessage({
                        id: 'EAS_CRUD_SOURCE_MSG_DELETE_ERROR',
                        defaultMessage: 'Chyba mazání dat: {detail}',
                    }, { detail: err.message });
                    showSnackbar(message, snackbar_types_1.SnackbarVariant.ERROR);
                    throw err;
                }
            }
        }
    });
    const refresh = event_callback_hook_1.useEventCallback(async () => {
        if (data !== null) {
            return handleGet(data.id);
        }
    });
    const reset = event_callback_hook_1.useEventCallback((data) => {
        if (fetch.current !== null) {
            fetch.current.abort();
            fetch.current = null;
        }
        setData(data !== null && data !== void 0 ? data : null);
    });
    return {
        url,
        data,
        loading,
        setLoading,
        get: handleGet,
        create: handleCreate,
        update: handleUpdate,
        del: handleDelete,
        refresh,
        reset,
    };
}
exports.useCrudSource = useCrudSource;
/**
 * Fetches data for single item from API.
 *
 * @param api API url
 */
function defaultGetItem(api, itemId) {
    return abortable_fetch_1.abortableFetch(`${api}/${itemId}`, {
        method: 'GET',
        headers: new Headers({ 'Content-Type': 'application/json' }),
    });
}
exports.defaultGetItem = defaultGetItem;
/**
 * Calls create API method.
 *
 * @param api API endpoint
 * @param item Object to save
 */
function defaultCreateItem(api, item) {
    return abortable_fetch_1.abortableFetch(api, {
        method: 'POST',
        headers: new Headers({
            'Content-Type': 'application/json',
        }),
        body: JSON.stringify(item),
    });
}
exports.defaultCreateItem = defaultCreateItem;
/**
 * Calls update API method.
 *
 *
 * @param api API endpoint
 * @param initialItem Initial data from last saved instance
 * @param item Object to save
 */
function defaultUpdateItem(api, item, _initialItem) {
    return abortable_fetch_1.abortableFetch(`${api}/${item.id}`, {
        headers: new Headers({
            'Content-Type': 'application/json',
        }),
        method: 'PUT',
        body: JSON.stringify(item),
    });
}
exports.defaultUpdateItem = defaultUpdateItem;
/**
 * Calls delete API method.
 *
 * @param api API endpoint
 * @param itemId Id of object
 */
function defaultDeleteItem(api, itemId) {
    return abortable_fetch_1.abortableFetch(`${api}/${itemId}`, {
        headers: new Headers({
            'Content-Type': 'application/json',
        }),
        method: 'DELETE',
    });
}
exports.defaultDeleteItem = defaultDeleteItem;
function getItemFactory({ getItemMethod = defaultGetItem, postProcess = (data) => data, }) {
    return function getItem(api, itemId) {
        const fetch = getItemMethod(api, itemId);
        const augmented = {
            response: fetch.response,
            abort: fetch.abort,
            json: async () => {
                const data = await fetch.json();
                const processedData = postProcess(data);
                if (processedData instanceof Promise) {
                    return await processedData;
                }
                else {
                    return processedData;
                }
            },
            text: async () => {
                throw new Error('Unsupported operation');
            },
            raw: async () => {
                throw new Error('Unsupported operation');
            },
            none: async () => {
                throw new Error('Unsupported operation');
            },
        };
        return augmented;
    };
}
exports.getItemFactory = getItemFactory;
function updateItemFactory({ updateItemMethod = defaultUpdateItem, preProcess = (data) => data, postProcess = (data) => data, }) {
    return function updateItem(api, item, initialItem) {
        const preprocessed = preProcess(item);
        const fetch = updateItemMethod(api, preprocessed, initialItem);
        const augmented = {
            response: fetch.response,
            abort: fetch.abort,
            json: async () => {
                const data = await fetch.json();
                const processedData = postProcess(data);
                if (processedData instanceof Promise) {
                    return await processedData;
                }
                else {
                    return processedData;
                }
            },
            text: async () => {
                throw new Error('Unsupported operation');
            },
            raw: async () => {
                throw new Error('Unsupported operation');
            },
            none: async () => {
                throw new Error('Unsupported operation');
            },
        };
        return augmented;
    };
}
exports.updateItemFactory = updateItemFactory;
function createItemFactory({ createItemMethod = defaultCreateItem, preProcess = (data) => data, postProcess = (data) => data, }) {
    return function createItem(api, item) {
        const preprocessed = preProcess(item);
        const fetch = createItemMethod(api, preprocessed);
        const augmented = {
            response: fetch.response,
            abort: fetch.abort,
            json: async () => {
                const data = await fetch.json();
                const processedData = postProcess(data);
                if (processedData instanceof Promise) {
                    return await processedData;
                }
                else {
                    return processedData;
                }
            },
            text: async () => {
                throw new Error('Unsupported operation');
            },
            raw: async () => {
                throw new Error('Unsupported operation');
            },
            none: async () => {
                throw new Error('Unsupported operation');
            },
        };
        return augmented;
    };
}
exports.createItemFactory = createItemFactory;
//# sourceMappingURL=crud-source-hook.js.map